import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
    Input,
    CalendarPicker,
    Button,
    Checkbox,
    CheckboxGroup,
    Radio,
    RadioGroup,
} from '@appkit4/react-components';
import { usePostV1AdminGetValidationConfig } from '../../../api/generated/endpoint';
import {
    FieldTypes,
    GetValidationConfigResponse,
} from '../../../api/generated/models';
import './OnlineFormFields.scss';
import { GetOnlineFormFieldsDTO } from '../../../api/generated/models';

interface OnlineFormFieldsProps {
    fields: GetOnlineFormFieldsDTO[];
    onUpdate: (field: string, value: any, checkboxSelectedValues: any) => void;
    onProceed: () => void;
    onNext: () => void;
    isSubmitting: boolean;
    isProceeding: boolean;
    hasNextDocument: boolean;
    hideSubmitButtons?: boolean;
    hideSectionNames?: boolean;
}

const CheckboxRadioField: React.FC<{
    fieldData: GetOnlineFormFieldsDTO;
    onUpdate: (field: string, value: any, checkboxSelectedValues: any) => void;
}> = ({ fieldData, onUpdate }) => {
    const {
        fieldName,
        value,
        checkboxAllValues,
        checkboxSelectedValues,
        fieldId,
    } = fieldData;

    const [selectedValues, setSelectedValues] = useState<(string | number)[]>(
        checkboxSelectedValues || []
    );
    const [validationConfig, setValidationConfig] =
        useState<GetValidationConfigResponse | null>(null);
    const mutation = usePostV1AdminGetValidationConfig();

    useEffect(() => {
        if (fieldId) {
            mutation.mutate({ data: { documentTypeFieldId: fieldId } });
        }
    }, [fieldId]);

    // Update validation config when mutation data changes
    useEffect(() => {
        if (mutation.data) {
            setValidationConfig(mutation.data as GetValidationConfigResponse);
        }
    }, [mutation.data]);

    // Update selected values when props change
    useEffect(() => {
        if (checkboxSelectedValues) {
            setSelectedValues(checkboxSelectedValues);
        }
    }, [checkboxSelectedValues]);

    const handleCheckboxChange = (newSelectedValues: (string | number)[]) => {
        setSelectedValues(newSelectedValues);
        onUpdate(fieldName ?? '', value, newSelectedValues);
    };

    const handleRadioChange = (val: string | number) => {
        handleCheckboxChange([val]);
    };

    // Determine if this should be a radio group (single selection)
    const isRadioGroup =
        validationConfig &&
        validationConfig.checkboxMinimumSelected === 1 &&
        validationConfig.checkboxMaximumSelected === 1;

    if (validationConfig && !isRadioGroup) {
        return (
            <CheckboxGroup
                value={selectedValues}
                onChange={handleCheckboxChange}
            >
                {(checkboxAllValues ?? []).map((option) => (
                    <Checkbox key={option} value={option}>
                        {option}
                    </Checkbox>
                ))}
            </CheckboxGroup>
        );
    }

    // Default to radio group
    return (
        <RadioGroup
            value={selectedValues.length > 0 ? selectedValues[0] : undefined}
            onChange={handleRadioChange}
        >
            {(checkboxAllValues ?? []).map((option) => {
                let displayOption = option;
                if (option === 'True') displayOption = 'Yes';
                if (option === 'False') displayOption = 'No';
                return (
                    <Radio key={option} value={option}>
                        {displayOption}
                    </Radio>
                );
            })}
        </RadioGroup>
    );
};

const OnlineFormFields: React.FC<OnlineFormFieldsProps> = ({
    onUpdate,
    onProceed,
    onNext,
    fields,
    isSubmitting,
    isProceeding,
    hasNextDocument,
    hideSubmitButtons = false,
    hideSectionNames = false,
}) => {
    // Create a map of fieldId to current values for condition evaluation
    const fieldValuesMap = useMemo(() => {
        const map = new Map<number, any>();
        fields.forEach((field) => {
            if (field.fieldId) {
                // For checkbox fields, use selected values
                if (
                    field.checkboxSelectedValues &&
                    field.checkboxSelectedValues.length > 0
                ) {
                    map.set(field.fieldId, field.checkboxSelectedValues[0]); // Use first selected value for conditions
                } else {
                    map.set(field.fieldId, field.value);
                }
            }
        });
        return map;
    }, [fields]);

    /**
     * Parse a condition string into top-level comma-separated parts,
     * respecting nested parentheses and quoted strings.
     */
    const parseConditionParts = (condition: string): string[] => {
        const parts: string[] = [];
        let current = '';
        let depth = 0;
        let inQuotes = false;

        for (let i = 0; i < condition.length; i++) {
            const char = condition[i];

            if (char === '"') {
                inQuotes = !inQuotes;
                current += char;
            } else if (!inQuotes) {
                if (char === '(') {
                    depth++;
                    current += char;
                } else if (char === ')') {
                    depth--;
                    current += char;
                } else if (char === ',' && depth === 0) {
                    // Top-level comma - split here
                    if (current.trim()) {
                        parts.push(current.trim());
                    }
                    current = '';
                } else {
                    current += char;
                }
            } else {
                current += char;
            }
        }

        // Add the last part
        if (current.trim()) {
            parts.push(current.trim());
        }

        return parts;
    };

    /**
     * Recursively evaluate a condition statement.
     * Supports nested AND/OR operators and simple equality checks.
     */
    const evaluateCondition = (
        conditionStatement: string | null | undefined
    ): boolean => {
        if (!conditionStatement) return true; // No condition means always show

        try {
            // Replace field references with actual values
            let evaluatedCondition = conditionStatement;

            // Handle field references like #1810
            const fieldReferences = conditionStatement.match(/#(\d+)/g);
            if (fieldReferences) {
                fieldReferences.forEach((ref) => {
                    const fieldId = parseInt(ref.substring(1));
                    const fieldValue = fieldValuesMap.get(fieldId);

                    // Convert value to string for comparison
                    const valueStr = fieldValue?.toString() || '';
                    evaluatedCondition = evaluatedCondition.replace(
                        ref,
                        `"${valueStr}"`
                    );
                });
            }

            // Now recursively evaluate the condition
            return evaluateExpression(evaluatedCondition.trim());
        } catch (error) {
            console.error(
                'Error evaluating condition:',
                conditionStatement,
                error
            );
            return true; // Show field by default if there's an error
        }
    };

    /**
     * Recursively evaluate an expression (after field substitution).
     */
    const evaluateExpression = (expr: string): boolean => {
        // Handle simple equality: "value"="True"
        const simpleEqualityMatch = expr.match(/^"([^"]*)"="([^"]*)"$/);
        if (simpleEqualityMatch) {
            return simpleEqualityMatch[1] === simpleEqualityMatch[2];
        }

        // Handle AND conditions: AND(...)
        if (expr.startsWith('AND(') && expr.endsWith(')')) {
            const innerContent = expr.substring(4, expr.length - 1);
            const conditions = parseConditionParts(innerContent);
            return conditions.every((cond) => evaluateExpression(cond.trim()));
        }

        // Handle OR conditions: OR(...)
        if (expr.startsWith('OR(') && expr.endsWith(')')) {
            const innerContent = expr.substring(3, expr.length - 1);
            const conditions = parseConditionParts(innerContent);
            return conditions.some((cond) => evaluateExpression(cond.trim()));
        }

        console.warn('Unable to parse expression:', expr);
        return true; // Default to showing field if we can't parse
    };

    // Filter fields based on their conditions
    const visibleFields = useMemo(() => {
        return fields.filter((field) =>
            evaluateCondition(field.conditionStatement)
        );
    }, [fields, fieldValuesMap]);

    const onDateChange =
        (field: string) => (date: Date | null, isValid: boolean) => {
            if (isValid && date) {
                const formattedDate = formatDateForAPI(date);
                onUpdate(field, formattedDate, null);
            }
        };

    const formatDateForAPI = (date: Date): string => {
        return date.toISOString();
    };

    const parseDateFromAPI = (dateString: string): Date | null => {
        try {
            const parsedDate = new Date(dateString);
            return isNaN(parsedDate.getTime()) ? null : parsedDate;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return null;
        }
    };

    const renderField = (fieldData: GetOnlineFormFieldsDTO) => {
        const { fieldName, value, checkboxAllValues, fieldTypeId } = fieldData;

        // For checkbox fields, use the separate component
        if (checkboxAllValues !== null) {
            return (
                <CheckboxRadioField fieldData={fieldData} onUpdate={onUpdate} />
            );
        }

        // Date field
        // if ((fieldName ?? '').toLowerCase().includes('date')) {
        if (fieldTypeId === FieldTypes.NUMBER_3) {
            const dateValue = value ? parseDateFromAPI(value as string) : null;
            return (
                <CalendarPicker
                    value={dateValue}
                    format="YYYY-MM-DD"
                    fieldTitle={fieldName ?? ''}
                    onChange={onDateChange(fieldName ?? '')}
                />
            );
        }

        // Default: Input
        return (
            <Input
                value={(value as string) || ''}
                onChange={(value: string) =>
                    onUpdate(fieldName ?? '', value, null)
                }
            />
        );
    };

    // Group visible fields by sectionName
    const sectionedFields = visibleFields.reduce<
        Record<string, GetOnlineFormFieldsDTO[]>
    >((acc, field) => {
        const section = field.sectionName || 'Other';
        if (!acc[section]) acc[section] = [];
        acc[section].push(field);
        return acc;
    }, {});

    return (
        <div className="processing-fields-container">
            <div className="processing-fields">
                {Object.entries(sectionedFields).map(
                    ([section, sectionFields]) => (
                        <div className="form-section" key={section}>
                            {!hideSectionNames && (
                                <h3 className="form-section-header">
                                    {section}
                                </h3>
                            )}
                            {sectionFields.map((field) => (
                                <div className="field-row" key={field.fieldId}>
                                    <div className="field-content">
                                        <div className="field-cell field-name">
                                            {field.fieldName}
                                            {field.isRequired && (
                                                <span
                                                    style={{
                                                        color: '#E0301E',
                                                        marginLeft: 6,
                                                    }}
                                                >
                                                    *
                                                </span>
                                            )}
                                        </div>
                                        <div className="field-cell extracted-value-cell">
                                            {renderField(field)}
                                            {field.validationErrorType !==
                                                1 && (
                                                <div className="confidence-indicator">
                                                    {field.validationErrorType ===
                                                    0 ? (
                                                        <span className="Appkit4-icon icon-warning-fill status-icon warning-icon custom-warning-icon" />
                                                    ) : field.validationErrorType ===
                                                      2 ? (
                                                        <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                                                    ) : null}
                                                    <span className="confidence-text">
                                                        {field.validationErrorType ===
                                                        2
                                                            ? 'System error'
                                                            : ''}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {field.validationErrorType === 1 && (
                                        <div className="validation-error">
                                            <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                                            <span className="confidence-text">
                                                {field.log ||
                                                    'Validation failed'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>
            {!hideSubmitButtons && (
                <div className="proceed-button-container">
                    <Button
                        variant="primary"
                        onClick={onProceed}
                        className="proceed-button"
                        loading={isSubmitting}
                        disabled={isSubmitting || isProceeding}
                        icon="icon-save-outline"
                    >
                        Submit
                    </Button>
                    <Button
                        variant="primary"
                        className="proceed-button"
                        onClick={onNext}
                        loading={isProceeding}
                        disabled={
                            isSubmitting || isProceeding || !hasNextDocument
                        }
                        icon="icon-arrow-right-outline"
                    >
                        Submit & Proceed to Next
                    </Button>
                </div>
            )}
        </div>
    );
};

export default OnlineFormFields;
